import { PaStatus } from "../../../enums/paStatus";
import { FilterCondition, FilterGroup } from "./buildAsteraSearchFilters";
import { AsteraBiosimilarFilterValue } from "./createFilterSections";

/**
 * Builds a single predicate condition node for BigQuery structured filters.
 *
 * @param field - Column name to filter on
 * @param operator - Comparison operator understood by the BigQuery API
 * @param value - Optional filter value
 * @return FilterCondition: One filter predicate node
 */
const buildCondition = (
  field: string,
  operator: string,
  value?: string | boolean | Array<string | boolean>,
): FilterCondition => {
  if (value === undefined) {
    return { type: "condition", field, operator };
  }

  return { type: "condition", field, operator, value };
};

/**
 * Builds an AND group of predicate nodes.
 *
 * @param children - Child filter nodes combined with AND
 * @return FilterGroup: AND filter group
 */
const buildAndGroup = (
  children: (FilterCondition | FilterGroup)[],
): FilterGroup => ({
  type: "group",
  operator: "and",
  children,
});

/**
 * Builds an OR group of predicate nodes.
 *
 * @param children - Child filter nodes combined with OR
 * @return FilterGroup: OR filter group
 */
const buildOrGroup = (
  children: (FilterCondition | FilterGroup)[],
): FilterGroup => ({
  type: "group",
  operator: "or",
  children,
});

/**
 * Predicate for rows that display the green "Yes" biosimilar badge.
 *
 * @return FilterGroup: parent_identifier and source_drug both present
 */
const buildYesBiosimilarPredicate = (): FilterGroup =>
  buildAndGroup([
    buildCondition("parent_identifier", "is_not_empty"),
    buildCondition("source_drug", "is_not_empty"),
  ]);

/**
 * Predicate for rows that display the yellow "Initiated" biosimilar badge.
 *
 * @return FilterGroup: parent row, no source drug, biosimilar denied case
 */
const buildInitiatedBiosimilarPredicate = (): FilterGroup =>
  buildAndGroup([
    buildCondition("parent_identifier", "is_not_empty"),
    buildCondition("source_drug", "is_empty"),
    buildCondition("is_biosimilar_case", "is_any_of", [true]),
    buildCondition("response_status", "is_any_of", [PaStatus.DENIED]),
  ]);

/**
 * Predicate for rows that display the grey "Null" biosimilar badge.
 *
 * @return FilterGroup: rows that are neither Yes nor Initiated
 */
const buildNullBiosimilarPredicate = (): FilterGroup =>
  buildAndGroup([
    buildOrGroup([
      buildCondition("parent_identifier", "is_empty"),
      buildCondition("source_drug", "is_empty"),
    ]),
    buildOrGroup([
      buildCondition("parent_identifier", "is_empty"),
      buildCondition("source_drug", "is_not_empty"),
      buildCondition("is_biosimilar_case", "is_not_any_of", [true]),
      buildCondition("response_status", "is_not_any_of", [PaStatus.DENIED]),
    ]),
  ]);

/**
 * Maps one biosimilar sidebar selection to its BigQuery predicate tree.
 *
 * @param value - Selected biosimilar filter value
 * @return FilterGroup | null: Predicate tree for the selection
 */
const buildPredicateForBiosimilarValue = (
  value: string,
): FilterGroup | null => {
  switch (value) {
    case AsteraBiosimilarFilterValue.YES:
      return buildYesBiosimilarPredicate();
    case AsteraBiosimilarFilterValue.INITIATED:
      return buildInitiatedBiosimilarPredicate();
    case AsteraBiosimilarFilterValue.NULL:
      return buildNullBiosimilarPredicate();
    default:
      return null;
  }
};

/**
 * Builds an OR predicate tree for the selected Biosimilar sidebar filter values.
 *
 * @param selectedValues - Selected biosimilar filter values from the sidebar
 * @return FilterGroup | null: Combined predicate tree, or null when nothing selected
 */
export const buildAsteraBiosimilarFilterTree = (
  selectedValues: string[],
): FilterGroup | null => {
  const predicates = selectedValues
    .map(buildPredicateForBiosimilarValue)
    .filter((predicate): predicate is FilterGroup => predicate != null);

  if (predicates.length === 0) {
    return null;
  }

  if (predicates.length === 1) {
    return predicates[0];
  }

  return buildOrGroup(predicates);
};
